import SignOffBar from "@/components/SignOffBar";   // Batch 11 · A2
import useMissingFields, { FieldMessage } from "@/lib/useMissingFields";   // Batch 27 · Add-on 1
import { fmtDateTimeFirm, fmtDate } from "@/lib/dt";   // 5c · F-5
import React, { useCallback, useEffect, useMemo, useState } from "react";
import useEffectEvent from "@/lib/useEffectEvent";   // Step 7c · E3: the effect runs on its own deps only (see the file)
import { asArray } from "@/lib/asArray";
import api, { API_URL, toErrMsg } from "@/lib/api";
import { useEngagement } from "@/contexts/EngagementContext";
import useMaterialityFigures from "@/lib/useMaterialityFigures";   // Step 3 · rule 3 (B-3a): the 105(a) figures, not the fields
import { useI18n } from "@/contexts/I18nContext";
import ContinuousRulesRun from "@/components/ContinuousRulesRun";   // Batch 27 · Add-on 2 Step 2
import { fmtPlural } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { AutoGrowTextarea } from "@/components/ui/auto-grow-textarea";   // Batch 23 · Stage 3: the reason shows in full
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useSearchParams } from "react-router-dom";
import { Trash2, Download, Play, Paperclip, RefreshCw, Upload, Layers, FolderTree } from "lucide-react";
import { LockedBanner } from "@/components/SignOffCard";
import Ref from "@/components/Ref";
import { toast } from "sonner";
import LayerLine from "@/components/LayerLine";   // Batch 32 · B32-1 — the layer sentence (was the LayerBadge pill)
import FullScreenDialog from "@/components/FullScreenDialog";
import AttachmentPanel from "@/components/AttachmentPanel";
import HeadwiseGLDialog from "@/pages/Sampling/HeadwiseGLDialog";
import ResampleDialog from "@/pages/Sampling/ResampleDialog";
import HeadTimelineDialog from "@/pages/Sampling/HeadTimelineDialog";
import PopulationPeriodLine from "@/pages/Sampling/PopulationPeriodLine";   // Batch 34 · Z-11
import ConfidenceField from "@/pages/Sampling/ConfidenceField";   // F3 (A-23): starts empty, no default level
import { SamplingConfidenceNote, SamplingEmNote, samplingReasonBody, useEngagementMethodology, useSamplingDefaults } from "@/components/methodology/MethodologyRange";   // STEP 7e-2 · S2
import { useResizableColumns, ResizableTh, ResetCaption, ResizableTable } from "@/components/ui/resizable-table";
import { askConfirm } from "@/lib/appDialog";   // Batch 15 · N4 (F-118)
import { fmtNumber, fmtCount } from "@/components/Amount";   // STEP 3 · rule 2 · B-2e: the one amount formatter
import { SkippedRowsDialog } from "@/pages/Sampling/SkippedRowsDialog";   // STEP 6d split: moved out unchanged
import { ContinuousPanel } from "@/pages/Sampling/ContinuousPanel";   // STEP 6d split
import { LockedFromMateriality } from "@/pages/Sampling/LockedFromMateriality";   // STEP 6d split
import EvaluationPanel from "@/pages/Sampling/EvaluationPanel";   // Step 7a: the evaluation of a sample (ISA 530)
import SampleMethodCell from "@/pages/Sampling/SampleMethodCell";   // Step 7a: method label + selection line + summary
import { methodText } from "@/pages/Sampling/evaluationFormat";   // Step 7a · S16: the method as it shows
import {
  CONTINUOUS_METHOD, arEntriesLabel, SPECIFIC_METHOD, SPECIFIC_REASONS, METHODS, CATEGORIES, THRESHOLD_RULE,
  confidencePctError, confidencePctToFraction, confidenceLabel,
} from "@/pages/Sampling/common";   // STEP 6d split: the page's constants and helpers
import { formForMethod, categoryForBody, ALL_CATEGORIES } from "@/pages/Sampling/specificCategory";   // STEP 7d · D-e
import SpecificCategoryNote from "@/pages/Sampling/SpecificCategoryNote";   // STEP 7d · D-e
import { sourceBadgeText, C3cStatsLines } from "@/pages/Sampling/c3cSampleLines";   // Step 11c · C3c · finds 16, 20, 21
export { confidencePctError, confidencePctToFraction, confidenceLabel };   // STEP 6d: still importable from @/pages/Sampling

export default function Sampling() {
  const colW = useResizableColumns({ storageKey: "sampling-population", defaults: [130, 160, 160, 160, 240, 130, 130, 130, 130, 130, 130], mins: [96, 100, 100, 100, 130, 90, 90, 90, 90, 90, 90] });   // Step 11d · add-on
  const { t, lang, dict } = useI18n();
  const { current, refresh: refreshEngagements } = useEngagement();
  const [searchParams, setSearchParams] = useSearchParams();
  const [form, setForm] = useState({
    name: "Sample 1", method: "MUS", category_filter: "Opex",
    confidence_pct: "", sample_size: "", strata: 4,   // Batch 20 · F-153: typed as a percentage (25–100), sent as a fraction; F3: starts empty, chosen by the auditor
    // Sampling source has NO default — auditor must pick one so an
    // accidental "wrong source" sample never happens silently.
    sampling_source: "",
  });
  const [samples, setSamples] = useState([]);
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState("");   // Batch 9 · A17 (F-70): the last refusal, shown next to the button
  const samplesCols = useResizableColumns({
    storageKey: "sampling/samples-list-v2",
    defaults: [260, 130, 130, 120, 110, 140, 360, 100, 140],
  });
  const [attachSample, setAttachSample] = useState(null);
  const [soOpen, setSoOpen] = useState("");   // Batch 11 · A2: the sample whose sign-off bar is open
  const [evalOpen, setEvalOpen] = useState(() => searchParams.get("sample") || "");   // Step 7a: the sample whose evaluation is open (W9 link-back ?sample=)
  const [headwiseOpen, setHeadwiseOpen] = useState(false);
  const [resampleTarget, setResampleTarget] = useState(null);
  const [headUploads, setHeadUploads] = useState([]);
  const [timelineHead, setTimelineHead] = useState(null);
  const [skippedFor, setSkippedFor] = useState(null);
  const [taxonomyByCode, setTaxonomyByCode] = useState({});
  // Batch 0 corrections round 3 — Sampling Wizard Selector.
  // No default pre-selected; required on submit for non-continuous methods.
  const [folderChoices, setFolderChoices] = useState([]);
  const [worksheetSources, setWorksheetSources] = useState([]);
  // H1 · M3h — derive JE (A9001) sources from the same sampling-sources
  // response. No new endpoint; `je_a9001` rows already live in
  // `sampling_sources`. Empty list when none — never `undefined`.
  const jeSources = useMemo(
    () => asArray(worksheetSources).filter((s) => s?.source_type === "je_a9001"),
    [worksheetSources]
  );
  // The worksheet block below only wants physical_count / completeness
  // rows; je_a9001 renders in its own block.
  const wsOnly = useMemo(
    () => asArray(worksheetSources).filter((s) => s?.source_type !== "je_a9001"),
    [worksheetSources]
  );
  const [fileInFolder, setFileInFolder] = useState("");
  const [jePending, setJePending] = useState(null);

  // -------- Continuous testing state --------
  const [continuousRules, setContinuousRules] = useState([]);   // [{key, count, source}]
  const [selectedRules, setSelectedRules] = useState(new Set());
  const [continuousScope, setContinuousScope] = useState("combined");
  const [thresholdAmount, setThresholdAmount] = useState("");
  const isContinuous = form.method === CONTINUOUS_METHOD;
  const isSpecific = form.method === SPECIFIC_METHOD;                     // Batch 9 · A19
  const [specificText, setSpecificText] = useState("");                  // one voucher per line, optionally "| reason"
  const mf = useMissingFields(lang);                                       // Batch 27 · Add-on 1
  const [specificReason, setSpecificReason] = useState("key_item");
  const [continuousScan, setContinuousScan] = useState(null);
  const [continuousReload, setContinuousReload] = useState(0);         // Batch 27 · Add-on 2 Step 2: after a Run / Rescan           // Batch 9 · A20: {scan_present, scan_at, no_scan_message}

  // Tolerable misstatement defaults to Performance Materiality, which comes
  // EXCLUSIVELY from 105(a) (the Materiality page). It is displayed here
  // read-only — the only way to change it is on 105(a). The number shown here
  // is exactly what the sampling engine will use (matches the "Materiality
  // Used" column in the Existing Samples table below).
  //
  // `linkedMateriality` is still sent to the backend on the request body for
  // backward-compat with `SampleCreate.materiality`, but the server ignores
  // it whenever 105(a) performance materiality is set.
  // We point it at OVERALL materiality here so that in the rare case where a
  // firm has only set overall materiality (and not performance), the sample
  // engine still has a materiality input to work with — instead of silently
  // receiving 0.
  // Step 3 · rule 3 (B-3a): from 105(a) (GET materiality-figures), never the engagement fields (a mirror that could be stale)
  const matFig = useMaterialityFigures(current?.id);
  const linkedMateriality = Number(matFig.overall_materiality || matFig.performance_materiality || 0);
  const linkedTolerable = Number(matFig.performance_materiality || 0);
  const meth = useEngagementMethodology(current?.id);   // STEP 7e-2 · S2: firm minimum confidence + default expected misstatement
  const [methReason, setMethReason] = useState("");
  useSamplingDefaults(meth.ms, form, setForm, linkedTolerable);

  // Round 8 · U2 — the sampling_source value becomes `ws:<id>` once
  // the auditor picks a worksheet-sourced population; the Category
  // filter is irrelevant there (it's a GL-column filter). We hide /
  // disable it so a stored "Opex" doesn't silently apply when the
  // population isn't a GL slice.
  const isWsSource = (form.sampling_source || "").startsWith("ws:");
  // Round 24d · N1 — JE (A9001) population: category filter does not apply; Continuous blocked.
  const isJeSource = (form.sampling_source || "").startsWith("je:");
  const catNa = isWsSource || isJeSource;
  const catNaReason = isJeSource ? t("sampling.categoryHiddenForJe") : t("sampling.categoryHiddenForWs");

  // Step 11c · C3c · find 23: a refusal belongs to the method it was given for — cleared on a method change; find 22: no folder
  // is carried into or out of Continuous testing (it filed the continuous sheet under the refused PPS's folder)
  const prevContinuous = React.useRef(isContinuous);
  useEffect(() => {
    setRunError("");
    if (prevContinuous.current !== isContinuous) { setFileInFolder(""); prevContinuous.current = isContinuous; }
  }, [form.method, isContinuous]);

  const engId = current?.id;   // Step 7c · E3: the loaders below read the engagement id only
  const loadSamples = useCallback(async () => {
    if (!engId) return;
    const r = await api.get(`/samples/${engId}`);
    setSamples(r.data);
  }, [engId]);

  const loadHeadUploads = useCallback(async () => {
    if (!engId) return;
    try {
      const r = await api.get(`/gl/${engId}/head-uploads`);
      setHeadUploads(r.data || []);
    } catch { setHeadUploads([]); }
    // Also load taxonomy once so we can show a human-readable FS head name
    try {
      const r = await api.get(`/fs/taxonomy`, { _silent: true });
      const map = {};
      asArray(r.data?.fs_lines).forEach((l) => { map[l.code] = l.line; });
      setTaxonomyByCode(map);
    } catch { /* leave empty */ }
  }, [engId]);

  const deleteHeadUpload = async (u) => {
    const label = `${u.mode.toUpperCase()} · ${u.fs_codes.join(", ")} · ${fmtDateTimeFirm(u.uploaded_at)}`;
    const warn = u.mode === "replace" && !u.undone_at
      ? t("sampling.deleteHeadWarn")
      : "";
    if (!await askConfirm(t("sampling.deleteHeadConfirm").replace("{warn}", warn).replace("{label}", label))) return;
    try {
      const r = await api.delete(`/gl/${current.id}/head-uploads/${u.id}`);
      toast.success(t("sampling.toasts.deletedGl").replace("{n}", r.data.gl_rows_deleted));
      await loadHeadUploads();
      await loadSamples();
    } catch (e) { toast.error(toErrMsg(e, t("sampling.toasts.deleteFailed"))); }
  };

  useEffect(() => { loadSamples(); loadHeadUploads(); }, [loadSamples, loadHeadUploads]);

  // Batch 0 corrections round 3 — fetch every FS head + A9001/A9002.
  // Step 7c · E3: once per engagement (as before); the deep-link params are read at that moment, a later URL change does not re-run it
  const loadSourcesAndDeepLink = useEffectEvent(() => {
    if (!current) return;
    api.get(`/engagements/${current.id}/sampling-folders`, { _silent: true })
      .then((r) => {
        const folders = r.data?.folders || [];
        setFolderChoices(folders);
        // Step 12-F3 · G8: /sampling?fs=<FS head> (the head folder's "03" row, the head-folder vouching dialog) pre-selects that
        // head's folder — before, the parameter was not read and the page opened with no folder chosen
        const fsHead = searchParams.get("fs");
        if (fsHead && folders.some((f) => f.code === fsHead)) setFileInFolder((cur) => cur || fsHead);
      })
      .catch(() => setFolderChoices([]));
    // Round 8 · U2 — pre-select the sender's own worksheet entry when
    // Sampling is deep-linked from a worksheet via `?ws=<ref>` (set
    // by Send-to-Sampling). Without this the first list entry
    // appeared pre-selected (your "Completeness — 1201 source
    // documents" screen), making the owner think the wrong source
    // was chosen.
    api.get(`/engagements/${current.id}/sampling/worksheet-sources`, { _silent: true })
      .then((r) => {
        const sources = r.data?.sources || [];
        setWorksheetSources(sources);
        const wsRef = searchParams.get("ws");
        // Round 24b · L1 — JE population handed over from A9001 (no DB write until Run sampling).
        const jeFlags = searchParams.get("je_flags");
        if (jeFlags) {
          api.get(`/engagements/${current.id}/a9001/filtered-population?flags=${encodeURIComponent(jeFlags)}`, { _silent: true })
            .then((fp) => {
              const rows = fp.data?.rows || [];
              setJePending({ flags: jeFlags.split(",").filter(Boolean), lines: fp.data?.lines || 0, vouchers: fp.data?.vouchers || 0,
                             amount: fp.data?.amount_total ?? rows.reduce((a, r) => a + Math.abs(Number(r.amount) || 0), 0), partial: false,
                             generated_at: fp.data?.generated_at || null,
                             j10_reason: searchParams.get("j10_reason") || "" });
              setForm((f) => ({ ...f, sampling_source: "je:pending" }));
            }).catch(() => {});
        }
        const srcId = searchParams.get("source");
        if (srcId && sources.find((s) => s.id === srcId)) {
          const picked = sources.find((s) => s.id === srcId);
          setForm((f) => ({ ...f, sampling_source: `${picked.source_type === "je_a9001" ? "je" : "ws"}:${srcId}` }));
        }
        if (wsRef) {
          const match = sources.find((s) => s.ref === wsRef);
          if (match) setForm((f) => ({ ...f, sampling_source: `ws:${match.id}` }));
        }
      })
      .catch(() => setWorksheetSources([]));
  });
  useEffect(() => { loadSourcesAndDeepLink(); }, [engId, loadSourcesAndDeepLink]);

  // Load continuous rules whenever the engagement changes OR the user
  // switches to the Continuous method. Reuses fraud + analytics engines
  // via /samples/continuous-rules/{eng} — no re-scan on the client.
  useEffect(() => {
    if (!engId || !isContinuous) return;
    api.get(`/samples/continuous-rules/${engId}`)
      .then((r) => { setContinuousRules(r.data.rules || []); setContinuousScan({ scan_present: !!r.data.scan_present, scan_at: r.data.scan_at, no_scan_message: r.data.no_scan_message, scan_population: r.data.scan_population, stored: r.data.stored || null }); })
      .catch(() => { setContinuousRules([]); setContinuousScan(null); });
  }, [engId, isContinuous, continuousReload]);

  // Deep-link support: /sampling?method=continuous&rule=<name>
  useEffect(() => {
    const m = searchParams.get("method");
    const r = searchParams.get("rule");
    if (m === "continuous" || (r && !m)) {
      setForm((f) => ({ ...f, method: CONTINUOUS_METHOD,
                          name: r ? `Continuous — ${r}` : (f.name || "Continuous") }));
      if (r) setSelectedRules(new Set([r]));
      // Clear the query so a refresh doesn't re-toggle on top of user edits.
      const sp = new URLSearchParams(searchParams);
      sp.delete("method"); sp.delete("rule");
      setSearchParams(sp, { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  // Keep Sampling values fresh when the user updates Materiality in Risk Assessment
  // in another tab — refetch the engagement whenever this page mounts / becomes
  // visible or when the engagement selection changes.
  useEffect(() => {
    if (!current) return;
    refreshEngagements?.();
    const onVisible = () => { if (document.visibilityState === "visible") refreshEngagements?.(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
    // eslint-disable-next-line
  }, [current?.id]);

  const run = async () => {
    if (!current) return;
    // Sampling source is mandatory — must be explicitly chosen.
    // Continuous testing doesn't use sampling_source (it filters by
    // rule, not by source), so gate the check on MUS/random only.
    if (!isContinuous && !form.sampling_source) {
      toast.error(t("sampling.toasts.pickSource"));
      return;
    }
    // S1 — worksheet-scoped source populates from the worksheet
    // listing directly; "File in folder" is auto-set on the backend.
    const _isWs = (form.sampling_source || "").startsWith("ws:");
    if (!isContinuous && !fileInFolder && !_isWs) {
      toast.error(t("samplingSelector.pickFolderRequired"));
      return;
    }
    if (isContinuous && selectedRules.size === 0) {
      toast.error(t("sampling.toasts.pickRule"));
      return;
    }
    if (isContinuous && selectedRules.has(THRESHOLD_RULE)
        && (!thresholdAmount || Number(thresholdAmount) <= 0)) {
      toast.error(t("sampling.toasts.enterThreshold"));
      return;
    }
    if (!isContinuous && confidencePctError(form.confidence_pct)) {   // Batch 20 · F-153 (B1): no request while invalid
      setRunError(t(confidencePctError(form.confidence_pct)));
      return;
    }
    setRunning(true);
    try {
      const body = {
        engagement_id: current.id,
        name: form.name, method: form.method,
        category_filter: categoryForBody(form),   // STEP 7d · D-e: "All categories" → null (every line)
        // Materiality kept for legacy backend compat; TM is now its own editable field.
        materiality: linkedMateriality,
        confidence_level: confidencePctToFraction(form.confidence_pct),   // stored as a fraction 0.25–1.0
        // V9 (Feb 2026): Tolerable Misstatement is auditor-editable, defaults to PM.
        tolerable_misstatement: form.tolerable_misstatement !== ""
            && form.tolerable_misstatement !== undefined
            && form.tolerable_misstatement !== null
          ? Number(form.tolerable_misstatement)
          : linkedTolerable,
        expected_misstatement: Number(form.expected_misstatement || 0),
        expected_misstatements_k: 0,
        accept_oversize: !!form.accept_oversize,
        tm_override_reason: form.tm_override_reason || null,
        allocation_method: form.allocation_method || "proportional",
        sample_size: form.sample_size ? Number(form.sample_size) : null,
        strata: Number(form.strata),
        sampling_source: form.sampling_source,
        file_in_folder: fileInFolder || null,
        methodology_reason: samplingReasonBody(meth.ms, form, methReason),   // STEP 7e-2 · S2: a level below the firm minimum
      };
      if (isSpecific) {
        // Batch 9 · A19: "SI-2025-00535" or "SI-2025-00535 | high_value" per line (comma-separated also read)
        const vouchers = []; const reasons = {};
        for (const raw of specificText.split(/[\n,;]+/)) {
          const [v, r] = raw.split("|").map((x) => (x || "").trim());
          if (!v) continue;
          vouchers.push(v);
          const key = (r || "").toLowerCase().replace(/[\s-]+/g, "_");
          const hit = SPECIFIC_REASONS.find(([k, en]) => k === key || en.toLowerCase() === (r || "").toLowerCase());
          if (hit) reasons[v] = hit[0];
        }
        body.specific_vouchers = vouchers; body.specific_reasons = reasons; body.specific_default_reason = specificReason;
        body.tolerable_misstatement = 0; body.materiality = 0;
      }
      if (isContinuous) {
        body.continuous_rules = Array.from(selectedRules);
        body.continuous_scope = selectedRules.size > 1 ? continuousScope : "combined";
        if (selectedRules.has(THRESHOLD_RULE) && thresholdAmount) {
          body.threshold_amount = Number(thresholdAmount);
        }
      }
      // Round 24b · L1 — JE population handed over without a write: create the pool now, on Run sampling.
      if (body.sampling_source === "je:pending" && jePending) {
        // Round 25 · 0b — never run on stale counts: re-read the current population first.
        const fp = await api.get(`/engagements/${current.id}/a9001/filtered-population?flags=${encodeURIComponent(jePending.flags.join(","))}`, { _silent: true });
        if ((fp.data?.lines || 0) !== jePending.lines || (fp.data?.generated_at || null) !== (jePending.generated_at || null)) {
          setJePending((p) => ({ ...p, lines: fp.data?.lines || 0, vouchers: fp.data?.vouchers || 0, amount: fp.data?.amount_total || 0, generated_at: fp.data?.generated_at || null }));
          toast.error(t("sampling.jePopulationChanged"));
          setRunning(false);
          return;
        }
        const sent = await api.post(`/engagements/${current.id}/a9001/send-to-sampling`,
                                    { tests: jePending.flags, j10_reason: jePending.j10_reason || "" });
        body.sampling_source = `je:${sent.data.sampling_source_id}`;
      }
      const r = await api.post("/samples", body);
      if (isSpecific && (r.data?.vouchers_not_in_category || []).length) {   // STEP 7d · D-e: named, but no line in the category
        toast.warning(t("s7dde.specific.notInCategory").replace("{list}", r.data.vouchers_not_in_category.join(", ")));
      }
      const sheets = r.data?.sheets;
      toast.success(sheets && sheets.length
        ? t("sampling.toasts.createdSheets").replace("{n}", sheets.length)
        : t("sampling.toasts.sampleGenerated"));
      await loadSamples();
      setRunError("");
    } catch (e) {
      // Batch 9 · A17 (F-70): the refusal stays on the screen next to the button (a toast alone vanished — "Run sampling
      // silently does nothing" on live), with the choices the message names
      const msg = toErrMsg(e, t("sampling.toasts.samplingFailed"));
      setRunError(msg);
      if (!mf.apply(e)) toast.error(msg);
    } finally {
      setRunning(false);
    }
  };

  const exportSample = async (id) => {
    const token = localStorage.getItem("af_token");
    const url = `${API_URL}/samples/${id}/export`;
    const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!resp.ok) {   // Batch 22 · PR A follow-up: a refused export (GL deleted / replaced → re-run first) is a clear message, not a broken file
      let body = {};
      try { body = await resp.json(); } catch {}
      toast.error(toErrMsg({ response: { data: body } }, t("sampling.toasts.exportFailed")));
      return;
    }
    const blob = await resp.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = `sample_${id}.xlsx`; a.click();
    URL.revokeObjectURL(a.href);
  };

  const exportHeadwise = async (s, fmt /* "csv" | "xlsx" */) => {
    const token = localStorage.getItem("af_token");
    const url = `${API_URL}/samples/${s.id}/headwise.${fmt}`;
    try {
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) {
        const tt = await resp.text();
        toast.error(t("sampling.toasts.headExportFailedShort").replace("{fmt}", fmt.toUpperCase()).replace("{msg}", tt.slice(0, 200)));
        return;
      }
      const rowCount = resp.headers.get("X-Row-Count") || "0";
      const blob = await resp.blob();
      const a = document.createElement("a");
      const safeName = (s.name || "sample").replace(/[^\w-]+/g, "_").slice(0, 60);
      a.href = URL.createObjectURL(blob);
      a.download = `${safeName}_headwise.${fmt}`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(t("sampling.toasts.downloaded")
        .replace("{n}", fmtCount(rowCount))
        .replace("{units}", fmtPlural(Number(rowCount), lang, dict.plurals.headwiseRows).replace(/^\d+\s*/, ""))
        .replace("{fmt}", fmt.toUpperCase()));
    } catch (e) {
      toast.error(t("sampling.toasts.headExportFailed").replace("{fmt}", fmt.toUpperCase()));
    }
  };

  if (!current) return <div className="text-sm text-muted-foreground">{t("common.noEng")}</div>;

  return (
    <div className="space-y-6" data-testid="sampling-page">
      <LockedBanner engagement={current} />
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t("sampling.title")}</h1>
          <LayerLine screen="sampling" />
          <p className="text-sm text-muted-foreground">{t("sampling.subtitle")}</p>
        </div>
        <Button variant="outline"
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                onClick={() => setHeadwiseOpen(true)}
                disabled={!!current?.signed_off}
                data-testid="upload-headwise-gl-btn">
          <Upload className="me-1 h-3 w-3" /> {t("sampling.uploadHeadwiseBtn")}
        </Button>
      </div>

      {headUploads.length > 0 && (
        <Card data-testid="head-uploads-card">
          <CardHeader><CardTitle className="text-base">{t("sampling.headUploadsTitle")}</CardTitle></CardHeader>
          <CardContent className="p-0">
            <ResizableTable cols={colW} wrap className="w-full text-xs">
              <thead className="border-b bg-slate-50 text-[10px] uppercase text-muted-foreground">
                <tr>
                  <ResizableTh index={0} className="px-3 py-1.5 text-start">{t("sampling.headCols.when")}</ResizableTh>
                  <ResizableTh index={1} className="px-3 py-1.5 text-start">{t("sampling.headCols.by")}</ResizableTh>
                  <ResizableTh index={2} className="px-3 py-1.5 text-start">{t("sampling.headCols.mode")}</ResizableTh>
                  <ResizableTh index={3} className="px-3 py-1.5 text-start">{t("sampling.headCols.fsHeads")}</ResizableTh>
                  <ResizableTh index={4} className="px-3 py-1.5 text-start">{t("sampling.headCols.headName")}</ResizableTh>
                  <ResizableTh index={5} className="px-3 py-1.5 text-end">{t("sampling.headCols.input")}</ResizableTh>
                  <ResizableTh index={6} className="px-3 py-1.5 text-end">{t("sampling.headCols.inserted")}</ResizableTh>
                  <ResizableTh index={7} className="px-3 py-1.5 text-end">{t("sampling.headCols.skipped")}</ResizableTh>
                  <ResizableTh index={8} className="px-3 py-1.5 text-end">{t("sampling.headCols.pool")}</ResizableTh>
                  <ResizableTh index={9} className="px-3 py-1.5 text-end">{t("sampling.headCols.replaced")}</ResizableTh>
                  <ResizableTh index={10} className="px-3 py-1.5 text-end">{t("sampling.headCols.actions")}</ResizableTh>
                </tr>
              </thead>
              <tbody>
                {asArray(headUploads).slice(0, 10).map((u) => (
                  <tr key={u.id} className="border-b" data-testid={`head-upload-row-${u.id}`}>
                    <td className="px-3 py-1">{fmtDateTimeFirm(u.uploaded_at)}</td>
                    <td className="px-3 py-1">{u.uploaded_by_name || "—"}</td>
                    <td className="px-3 py-1">
                      <span className={`rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase ${
                        u.mode === "replace"
                          ? "bg-amber-100 text-amber-800"
                          : "bg-emerald-100 text-emerald-800"
                      }`}>{u.mode}</span>
                    </td>
                    <td className="px-3 py-1 font-mono text-[10px]">
                      <div className="flex flex-wrap items-center gap-1">
                        {asArray(u.fs_codes).slice(0, 6).map((c) => (
                          <button key={c}
                                  onClick={() => setTimelineHead(c)}
                                  className="rounded border border-slate-300 bg-slate-50 px-1 py-0.5 hover:border-emerald-400 hover:bg-emerald-50"
                                  title={t("sampling.viewTimelineTitle").replace("{code}", c)}
                                  data-testid={`head-timeline-btn-${c}`}>
                            {c}
                          </button>
                        ))}
                        {asArray(u.fs_codes).length > 6 && (
                          <span className="text-[10px] text-muted-foreground">
                            +{asArray(u.fs_codes).length - 6}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-1 text-[10px]"
                        data-testid={`head-upload-name-${u.id}`}>
                      {asArray(u.fs_codes).map((c) => taxonomyByCode[c] || "—").slice(0, 3).join(", ")}
                      {asArray(u.fs_codes).length > 3 && <span className="text-muted-foreground"> +{asArray(u.fs_codes).length - 3}</span>}
                    </td>
                    <td className="px-3 py-1 num-cell"
                        data-testid={`head-upload-input-${u.id}`}>
                      {fmtCount(u.input_rows ?? u.inserted ?? 0)}
                    </td>
                    <td className="px-3 py-1 num-cell"
                        data-testid={`head-upload-inserted-${u.id}`}>
                      {fmtCount(u.inserted || 0)}
                    </td>
                    <td className="px-3 py-1 num-cell">
                      {(() => {
                        const sOOS = u.skipped_out_of_scope || 0;
                        const sBAD = u.skipped_bad_row || 0;
                        const sMISS = u.skipped_missing_code || 0;
                        const sk = sOOS + sBAD + sMISS;
                        if (!sk) return <span className="text-muted-foreground">0</span>;
                        // Colour cue: red when the dominant reason (>50%) is
                        // out-of-scope / unmapped — the fix is deterministic
                        // (map the accounts, re-upload) and needs an actionable
                        // signal. Amber for the other reasons.
                        const dominantOOS = sOOS / sk > 0.5;
                        const cls = dominantOOS
                          ? "bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-200"
                          : "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200";
                        return (
                          <button type="button"
                                  onClick={() => setSkippedFor(u.id)}
                                  className={`rounded ${cls} px-1.5 py-0.5 text-[10px] font-semibold underline-offset-2 hover:underline`}
                                  title={dominantOOS
                                    ? t("sampling.dominantSkippedTitle").replace("{n}", fmtCount(sOOS)).replace("{total}", fmtCount(sk))
                                    : t("sampling.skippedGenericTitle")}
                                  data-testid={`head-upload-skipped-${u.id}`}>
                            {fmtCount(sk)}
                            {dominantOOS && <span className="ms-1 text-[9px] font-bold uppercase">{t("sampling.unmappedShort")}</span>}
                          </button>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-1 num-cell"
                        data-testid={`head-upload-pool-${u.id}`}
                        title={t("sampling.livePoolTitle")}>
                      {fmtCount(u.current_row_count ?? u.inserted ?? 0)}
                    </td>
                    <td className="px-3 py-1 num-cell">
                      {fmtCount(u.replaced_count || 0)}
                      {u.undone_at && (
                        <span className="ms-1 rounded bg-slate-100 px-1 py-0.5 text-[9px] font-semibold uppercase text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                              title={t("sampling.undoneTooltip").replace("{when}", fmtDateTimeFirm(u.undone_at)).replace("{who}", u.undone_by_name || "—")}>
                          {t("sampling.undoneTag")}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-1 text-end">
                      <Button size="sm" variant="outline"
                              className="border-red-400 text-red-700 hover:bg-red-50 dark:border-red-800 dark:text-red-300"
                              disabled={!!current?.signed_off}
                              onClick={() => deleteHeadUpload(u)}
                              data-testid={`head-upload-delete-${u.id}`}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </ResizableTable>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle className="text-base">{t("sampling.params")}</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <div>
            <Label>{t("sampling.sampleName")}</Label>
            <Input data-testid="samp-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <Label>{t("sampling.method")}</Label>
            <Select value={form.method} onValueChange={(v) => setForm(formForMethod(form, v))}>
              <SelectTrigger data-testid="samp-method"><SelectValue /></SelectTrigger>
              <SelectContent>{METHODS.map((m) => {
                const blocked = (isWsSource || isJeSource) && (m === CONTINUOUS_METHOD || m === SPECIFIC_METHOD);
                return <SelectItem key={m} value={m} disabled={blocked} data-testid={`samp-method-${m}`}>{methodText(m, t)}{blocked ? ` — ${t("sampling.wsContinuousBlocked")}` : ""}</SelectItem>;
              })}</SelectContent>
            </Select>
          </div>
          <div className={catNa ? "opacity-50 pointer-events-none" : ""}
               title={catNa ? catNaReason : undefined}
               data-testid="samp-cat-wrap">
            <Label>{t("sampling.category")}</Label>
            <Select value={catNa ? "__all__" : form.category_filter}
                     onValueChange={(v) => setForm({ ...form, category_filter: v })}
                     disabled={catNa}>
              <SelectTrigger data-testid="samp-cat"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_CATEGORIES} data-testid="samp-cat-all">{isSpecific ? t("s7dde.specific.allCategories") : t("sampling.allCategoriesTag")}</SelectItem>
                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            {catNa && (
              <div className="mt-0.5 text-[10px] text-muted-foreground"
                   data-testid={isJeSource ? "samp-cat-je-note" : "samp-cat-ws-note"}>
                {catNaReason}
              </div>
            )}
          </div>
          {!isContinuous && (
            <>
              <ConfidenceField value={form.confidence_pct} onChange={(v) => setForm({ ...form, confidence_pct: v })}
                               error={confidencePctError(form.confidence_pct)} t={t}>
                {/* STEP 7e-2 · S2: the firm minimum + the reason, under the level in its own cell */}
                <SamplingConfidenceNote ms={meth.ms} form={form} reason={methReason} setReason={setMethReason} mf={mf} t={t} />
              </ConfidenceField>
              <div>
                <Label>{t("sampling.pmReadOnly")}</Label>
                <LockedFromMateriality value={linkedTolerable} testid="samp-pm" />
              </div>
              <div>
                <Label>{t("sampling.tmEditable")}</Label>
                <Input type="number" data-testid="samp-tm"
                       value={form.tolerable_misstatement ?? ""}
                       placeholder={String(linkedTolerable || "")}
                       onChange={(e) => setForm({ ...form, tolerable_misstatement: e.target.value })} />
                <div className="mt-0.5 text-[10px] text-muted-foreground">
                  {t("sampling.tmHelp")}
                </div>
              </div>
              <div>
                <Label>{t("sampling.expMisstate")}</Label>
                <Input type="number" data-testid="samp-em"
                       value={form.expected_misstatement ?? "0"}
                       onChange={(e) => setForm({ ...form, expected_misstatement: e.target.value })} />
                <SamplingEmNote ms={meth.ms} t={t} />
              </div>
              {form.method === "MUS" && (
                <>
                  <div>
                    <Label>{t("sampling.acceptOversize")}</Label>
                    <div className="flex items-center gap-2 pt-2">
                      <Checkbox id="accept-oversize"
                                checked={!!form.accept_oversize}
                                onCheckedChange={(v) => setForm({ ...form, accept_oversize: !!v })}
                                data-testid="samp-oversize" />
                      <Label htmlFor="accept-oversize" className="text-xs text-muted-foreground">
                        {t("sampling.oversizeHelp")}
                      </Label>
                    </div>
                  </div>
                  <div>
                    <Label>{t("sampling.tmReason")}</Label>
                    <AutoGrowTextarea data-testid="samp-tm-reason"
                           value={form.tm_override_reason ?? ""}
                           placeholder={t("sampling.tmReasonPh")}
                           onChange={(e) => setForm({ ...form, tm_override_reason: e.target.value })} />
                  </div>
                </>
              )}
              <div>
                <Label>{t("sampling.sampleSize")}</Label>
                <Input type="number" data-testid="samp-size" value={form.sample_size} onChange={(e) => setForm({ ...form, sample_size: e.target.value })} />
                <div className="mt-0.5 text-[10px] text-muted-foreground">
                  {t("sampling.sampleSizeHelp")}
                </div>
              </div>
              {form.method === "Stratified" && (
                <div>
                  <Label>{t("sampling.strata")}</Label>
                  <Input type="number" data-testid="samp-strata" value={form.strata} onChange={(e) => setForm({ ...form, strata: e.target.value })} />
                </div>
              )}
              {form.method === "Stratified" && (
                <div>
                  <Label>{t("sampling.allocMethod")}</Label>
                  <Select value={form.allocation_method || "proportional"}
                          onValueChange={(v) => setForm({ ...form, allocation_method: v })}>
                    <SelectTrigger data-testid="samp-alloc"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="proportional">{t("sampling.allocProportional")}</SelectItem>
                      <SelectItem value="neyman">{t("sampling.allocNeyman")}</SelectItem>
                      <SelectItem value="equal">{t("sampling.allocEqual")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>
                  {t("sampling.samplingSource")} <span className="text-red-600" aria-hidden>*</span>
                </Label>
                <Select value={form.sampling_source}
                         onValueChange={(v) => {
                           setForm({ ...form, sampling_source: v });
                           // Round 21 · A2 — default "File in folder" to the source worksheet's head folder
                           if (v.startsWith("ws:")) {
                             const ws = asArray(worksheetSources).find((s) => `ws:${s.id}` === v);
                             const fs = ws?.fs_code || (ws?.worksheet_ref || "").split("-")[0];
                             if (fs) setFileInFolder(fs);
                           }
                         }}>
                  <SelectTrigger data-testid="samp-source"
                                    className={!form.sampling_source ? "border-amber-400" : ""}>
                    <SelectValue placeholder={t("sampling.sourceSelectPh")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="combined" data-testid="samp-source-combined">
                      {t("sampling.sourceCombined")}
                    </SelectItem>
                    <SelectItem value="maingl_only" data-testid="samp-source-maingl">
                      {t("sampling.sourceMainOnly")}
                    </SelectItem>
                    <SelectItem value="headwise_only" data-testid="samp-source-hw">
                      {t("sampling.sourceHeadOnly")}
                    </SelectItem>
                    {jePending && (
                      <SelectItem value="je:pending" data-testid="samp-source-je-pending">
                        {`Journal entries — ${jePending.flags.join(", ")} · ${fmtCount(jePending.lines)} lines · ${fmtCount(jePending.vouchers)} vouchers`}
                      </SelectItem>
                    )}
                    {wsOnly.length > 0 && (
                      <div className="mt-1 border-t px-2 pb-1 pt-1 text-[10px] font-semibold uppercase text-muted-foreground"
                           data-testid="samp-source-ws-header">
                        {t("sampling.sourceWorksheet")}
                      </div>
                    )}
                    {wsOnly.map((ws) => {
                      const isPC = ws.source_type === "physical_count" || /Physical count/i.test(ws.name || "") || ws.kind === "physical_count";
                      const isCP = ws.source_type === "completeness" || /Completeness/i.test(ws.name || "") || ws.kind === "completeness";
                      // Round 8 · U2 — label disambiguates two
                      // physical-count entries on the same screen
                      // by stitching worksheet ref + filename(s) +
                      // uploaded date + row count. Refs LTR-isolated.
                      const ref = ws.ref || (ws.name || "").match(/\((\d[\w-]*)\)/)?.[1] || (ws.name || "").match(/—\s*(\d[\w-]*)/)?.[1] || "";
                      const kindText = isPC ? (t("samplingLabels.physicalCountKind") || "Physical count")
                                                 : isCP ? (t("samplingLabels.completenessKind") || "Completeness")
                                                 : (ws.name || "");
                      const names = Array.isArray(ws.filenames) ? ws.filenames.filter(Boolean) : [];
                      const first = names[0] || "";
                      const more = names.length > 1 ? ` (+${names.length - 1} ${t("samplingLabels.moreFiles") || "file(s)"})` : "";
                      const date = fmtDate(ws.uploaded_at);
                      const rowsText = t("samplingLabels.rowsUnit").replace("{n}", ws.population_size);
                      const refSpan = ref ? <>· <span dir="ltr">{ref}</span> </> : null;
                      return (
                        <SelectItem key={ws.id} value={`ws:${ws.id}`}
                                      data-testid={`samp-source-ws-${ws.id}`}>
                          {kindText} {refSpan}
                          {first && <>· <span dir="ltr">{first}{more}</span> </>}
                          {date && <>· <span dir="ltr">{date}</span> </>}
                          · {rowsText}
                        </SelectItem>
                      );
                    })}
                    {/* F5 · M3h — Journal entries (A9001) sources. */}
                    {asArray(jeSources).map((js) => {
                      const n = js.row_count || 0;
                      const plural = n === 1 ? t("samplingLabels.journalEntriesUnitOne") : t("samplingLabels.journalEntriesUnitMany");
                      const summary = (js.name || "").match(/—\s*(.+?)\s*·/)?.[1] || "all";
                      const label = t("samplingLabels.journalEntries").replace("{summary}", summary).replace("{n}", n).replace("{plural}", plural);
                      return (
                        <SelectItem key={js.id} value={`je:${js.id}`}
                                      data-testid={`samp-source-je-${js.id}`}>
                          {label}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                {jePending && form.sampling_source === "je:pending" && (
                  <div className="mt-1 rounded border border-sky-200 bg-sky-50 p-2 text-[11px] text-sky-900" data-testid="samp-je-population-card">
                    <div className="font-semibold">Journal entries — {t("sampling.jeFiltersUsed")}: {jePending.flags.join(", ")}</div>
                    <div>{fmtCount(jePending.lines)} {t("sampling.jeLines")} · {fmtCount(jePending.vouchers)} {t("sampling.jeVouchers")} · {t("sampling.jeAmount")} {fmtNumber(jePending.amount)}{jePending.partial ? " (first 1,000 lines)" : ""}</div>
                    <div className="text-muted-foreground">{t("sampling.jeNoWrite")}</div>
                  </div>
                )}
                <div className="mt-0.5 text-[10px] text-muted-foreground">
                  {t("sampling.sourceRequired")}
                </div>
              </div>
              <div>
                <Label>
                  {t("samplingSelector.fileInFolder")} <span className="text-red-600" aria-hidden>*</span>
                </Label>
                <Select value={fileInFolder} onValueChange={setFileInFolder}>
                  <SelectTrigger data-testid="samp-file-in-folder"
                                    className={`h-auto min-h-9 whitespace-normal text-start [&>span]:line-clamp-none ${!fileInFolder ? "border-amber-400" : ""}`}>
                    <SelectValue placeholder={t("samplingSelector.fileInFolderPh")} />
                  </SelectTrigger>
                  <SelectContent>
                    {folderChoices.length === 0 && (
                      <div className="px-2 py-1.5 text-xs text-muted-foreground">
                        {t("samplingSelector.noFoldersHint")}
                      </div>
                    )}
                    {folderChoices.filter((f) => f.kind === "fs_head").map((f) => {
                      const lbl = arEntriesLabel(f.entries_count, lang, t);
                      return (
                      <SelectItem key={f.code} value={f.code}
                                    data-testid={`samp-folder-${f.code}`}>
                        <Ref>{f.code}</Ref> — {f.label}{" · "}
                        <span className="text-muted-foreground">
                          {lbl.number != null && <><Ref>{lbl.number}</Ref>{" "}</>}
                          {lbl.suffix}
                        </span>
                      </SelectItem>
                      );
                    })}
                    {folderChoices.filter((f) => f.kind === "special").map((f) => (
                      <SelectItem key={f.code} value={f.code}
                                    data-testid={`samp-folder-${f.code}`}>
                        <FolderTree className="me-1 inline h-3 w-3" /> {f.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="mt-0.5 text-[10px] text-muted-foreground">
                  {t("samplingSelector.fileInFolderHint")}
                </div>
              </div>
            </>
          )}
          {isSpecific && (
            <div className="md:col-span-4 rounded border border-sky-300 bg-sky-50/40 p-3 text-xs dark:bg-sky-950/20" data-testid="samp-specific-panel">
              <div className="font-medium">{methodText(SPECIFIC_METHOD, t)}</div>
              <div className="text-[11px] text-muted-foreground" data-testid="samp-specific-line">
                {lang === "ar" ? "اختيار بنود محددة — ليس عينة؛ لا تُسقَط النتائج على المجتمع" : "Selecting specific items — not sampling; results are not projected"}
              </div>
              <label className="mt-2 block">
                {lang === "ar" ? "أرقام القيود / الفواتير — رقم في كل سطر، ويمكن إضافة السبب بعد |" : "Voucher numbers — one per line, optionally the reason after a | (for example SI-2025-00535 | high value)"}
                <textarea rows={5} value={specificText} onChange={(e) => { setSpecificText(e.target.value); mf.filled("specific_vouchers", e.target.value); }}
                          {...mf.props("specific_vouchers", "mt-1 w-full rounded border px-2 py-1 font-mono text-[11px] dark:bg-slate-900")} data-testid="samp-specific-vouchers"
                          placeholder={"SI-2025-00535\nSI-2025-00915 | high value"} />
              </label>
              <FieldMessage mf={mf} field="specific_vouchers" testid="samp-specific-vouchers-missing" />
              <SpecificCategoryNote form={form} t={t} />
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span>{lang === "ar" ? "السبب الافتراضي للاختيار:" : "Default reason for selection:"}</span>
                <select value={specificReason} onChange={(e) => { setSpecificReason(e.target.value); mf.clear("specific_default_reason"); }} {...mf.props("specific_default_reason", "rounded border px-1 py-0.5 dark:bg-slate-900")} data-testid="samp-specific-reason">
                  {SPECIFIC_REASONS.map(([k, en, ar]) => <option key={k} value={k}>{lang === "ar" ? ar : en}</option>)}
                </select>
                <span className="text-muted-foreground">{lang === "ar" ? "المجتمع: دفتر الأستاذ الرئيسي، أو حسب البند، أو كلاهما (مصدر العينة أعلاه)؛ يُحفظ في المجلد -04" : "Population: main GL, head-wise or both (the source above); the sheet files under -04 and reads 'Not a sample — no projection'"}</span>
              </div>
            </div>
          )}
          {isContinuous && (   /* Step 11c · C3c · find 22: continuous testing has its own (optional) "File in folder" */
            <div data-testid="samp-continuous-folder">
              <Label>{t("s11c3c.sampling.folderContinuous")}</Label>
              <Select value={fileInFolder || "__none__"} onValueChange={(v) => setFileInFolder(v === "__none__" ? "" : v)}>
                <SelectTrigger data-testid="samp-file-in-folder" className="h-auto min-h-9 whitespace-normal text-start [&>span]:line-clamp-none">
                  <SelectValue placeholder={t("samplingSelector.fileInFolderPh")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__" data-testid="samp-folder-none">—</SelectItem>
                  {folderChoices.map((f) => (
                    <SelectItem key={f.code} value={f.code} data-testid={`samp-folder-${f.code}`}>
                      {f.kind === "fs_head" ? <><Ref>{f.code}</Ref> — {f.label}</> : f.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="mt-0.5 text-[10px] text-muted-foreground">{t("s11c3c.sampling.folderContinuousHint")}</div>
            </div>
          )}
          {isContinuous && current && (
            <ContinuousRulesRun engId={current.id} lang={lang} stored={continuousScan?.stored} status={continuousScan}
                                onDone={() => setContinuousReload((n) => n + 1)} />
          )}
          {isContinuous && (
            <ContinuousPanel
              scan={continuousScan}
              rules={continuousRules}
              selected={selectedRules}
              onToggle={(k) => {
                const s = new Set(selectedRules);
                if (s.has(k)) s.delete(k); else s.add(k);
                setSelectedRules(s);
              }}
              scope={continuousScope} onScope={setContinuousScope}
              thresholdAmount={thresholdAmount}
              onThreshold={setThresholdAmount}
            />
          )}
          <div className="md:col-span-4">
            <Button onClick={run}
                       disabled={running
                                    || (isContinuous && selectedRules.size === 0)
                                    || (!isContinuous && !form.sampling_source)}
                       className="bg-slate-900 hover:bg-slate-800" data-testid="samp-run">
              <Play className="me-1 h-4 w-4" /> {running ? t("common.loading") : (isContinuous ? t("sampling.runContinuous") : t("sampling.run"))}
            </Button>
            {runError && (
              <div className="mt-2 rounded border border-red-300 bg-red-50 p-2 text-xs text-red-900 dark:bg-red-950/30 dark:text-red-200" data-testid="samp-run-error" role="alert">
                <div className="font-medium">{lang === "ar" ? "رُفض التشغيل:" : "The run was refused:"} <span className="font-normal">{runError}</span></div>
                {/n=\d+ exceeds|exceeds the usual operational limit/i.test(runError) && (
                  <div className="mt-1 space-y-1">
                    <div>{lang === "ar" ? "اختر واحدًا من الخيارات الثلاثة:" : "Pick one of the three choices:"}</div>
                    <div className="flex flex-wrap gap-2">
                      <button type="button" className="rounded border bg-white px-2 py-0.5 dark:bg-slate-900" data-testid="samp-refusal-allow"
                              onClick={() => { setForm((f) => ({ ...f, accept_oversize: true })); setRunError(""); }}>
                        {lang === "ar" ? "السماح بـ n > 250 ثم التشغيل مرة أخرى" : "Tick 'Allow n > 250', then run again"}</button>
                      <span className="self-center">{lang === "ar" ? "· أو خفّض مستوى الثقة" : "· or lower the confidence level"}</span>
                      <span className="self-center">{lang === "ar" ? "· أو ارفع التحريف المقبول (مع سبب)" : "· or raise the tolerable misstatement (with a reason)"}</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">{t("sampling.existing")}</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="table-sticky-box overflow-x-auto min-w-0">
          <table className="w-full text-sm"
                 style={{ tableLayout: "fixed", width: "100%" }}>
            <ResetCaption cols={samplesCols} />
            <colgroup>
              {samplesCols.colWidths.map((w, i) => (
                <col key={i} style={{ width: w }} />
              ))}
            </colgroup>
            <thead className="border-b bg-slate-50 text-xs uppercase text-muted-foreground">
              <tr>
                <ResizableTh index={0} onResizeStart={samplesCols.onResizeStart} className="px-4 py-2 text-start">{t("sampling.cols.name")}</ResizableTh>
                <ResizableTh index={1} onResizeStart={samplesCols.onResizeStart} className="px-4 py-2 text-start">{t("sampling.method")}</ResizableTh>
                <ResizableTh index={2} onResizeStart={samplesCols.onResizeStart} className="px-4 py-2 text-end">{t("sampling.cols.materialityUsed")}</ResizableTh>
                <ResizableTh index={3} onResizeStart={samplesCols.onResizeStart} className="px-4 py-2 text-end">{t("sampling.cols.population")}</ResizableTh>
                <ResizableTh index={4} onResizeStart={samplesCols.onResizeStart} className="px-4 py-2 text-end">{t("sampling.cols.sample")}</ResizableTh>
                <ResizableTh index={5} onResizeStart={samplesCols.onResizeStart} className="px-4 py-2 text-start">{t("sampling.cols.uidRange")}</ResizableTh>
                <ResizableTh index={6} onResizeStart={samplesCols.onResizeStart} className="px-4 py-2 text-start">{t("sampling.cols.stats")}</ResizableTh>
                <ResizableTh index={7} onResizeStart={samplesCols.onResizeStart} className="px-4 py-2 text-end">{t("sampling.cols.dedupe")}</ResizableTh>
                <ResizableTh index={8} onResizeStart={samplesCols.onResizeStart} className="px-4 py-2 text-end">{t("common.actions")}</ResizableTh>
              </tr>
            </thead>
            <tbody>
              {samples.map((s) => (
                <React.Fragment key={s.id}>
                <tr className={`border-b hover:bg-slate-50 dark:hover:bg-slate-900/50 ${s.needs_rerun ? "bg-amber-50/60 dark:bg-amber-950/20" : ""}`} data-testid={`sample-row-${s.id}`}>
                  <td className="px-4 py-2 font-medium">
                    <div className="flex items-center gap-2">
                      <span>{s.name}</span>
                      {s.needs_rerun && (
                        <span
                          title={s.stale_reason || "Re-run this sheet — counts predate the fraud-engine fix."}
                          className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-800 dark:border-amber-800 dark:bg-amber-950/60 dark:text-amber-200"
                          data-testid={`sample-rerun-badge-${s.id}`}
                        >
                          {t("sampling.reRunNeeded")}
                        </span>
                      )}
                      {s.parent_sample_id && (
                        <span className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-200"
                              data-testid={`sample-resample-badge-${s.id}`}
                              title={`Re-sampled from parent · ${s.resample_type || ""}${s.resample_justification ? ` — ${s.resample_justification}` : ""}`}>
                          {t("sampling.resampleTag")} {s.resample_type || "child"}
                        </span>
                      )}
                      {s.stats?.voucher_coverage?.placeholder > 0 && (
                        <span className="inline-flex items-center gap-1 rounded-full border border-orange-300 bg-orange-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-orange-700 dark:bg-orange-950/40 dark:text-orange-200"
                              title={`${s.stats.voucher_coverage.placeholder} of ${s.stats.voucher_coverage.total} sampled rows (${s.stats.voucher_coverage.placeholder_pct}%) had no voucher_no in the source GL. Placeholder V-#### numbers were generated at sample time. Fix: re-import the GL with the voucher_no column mapped in the column-mapper.`}
                              data-testid={`sample-voucher-gap-${s.id}`}>
                          Voucher gap · {s.stats.voucher_coverage.placeholder_pct}%
                        </span>
                      )}
                      {s.stats?.headwise_coverage?.any && (
                        <span className="inline-flex items-center gap-1 rounded-full border border-purple-300 bg-purple-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-purple-700 dark:bg-purple-950/40 dark:text-purple-200"
                              title={`${s.stats.headwise_coverage.row_count} of ${s.sample_size} sampled rows came from head-wise uploads`}
                              data-testid={`sample-hw-coverage-${s.id}`}>
                          HW: {asArray(s.stats?.headwise_coverage?.fs_codes).slice(0, 4).join(", ") || "yes"}
                          {asArray(s.stats?.headwise_coverage?.fs_codes).length > 4 && ` +${asArray(s.stats?.headwise_coverage?.fs_codes).length - 4}`}
                        </span>
                      )}
                      {s.stats?.sampling_source && s.stats.sampling_source !== "combined" && (
                        <span className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-700 dark:bg-slate-800 dark:text-slate-200"
                              data-testid={`sample-source-${s.id}`}
                              title={t("sampling.populationFromTitle").replace("{src}", s.stats.sampling_source)}>
                          {sourceBadgeText(s.stats.sampling_source, t)}
                        </span>
                      )}
                    </div>
                    {s.needs_rerun && s.stale_reason && (
                      <div className="mt-0.5 max-w-md text-[10px] leading-tight text-amber-800 dark:text-amber-300"
                           data-testid={`sample-rerun-reason-${s.id}`}>
                        {(lang === "ar" && s.stale_reason_ar) ? s.stale_reason_ar : s.stale_reason}
                      </div>
                    )}
                    {s.parent_sample_id && s.resample_justification && (
                      <div className="mt-0.5 max-w-md text-[10px] leading-tight text-emerald-700 dark:text-emerald-300"
                           data-testid={`sample-resample-just-${s.id}`}>
                        {s.resample_justification}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2"><SampleMethodCell s={s} t={t} /></td>
                  <td className="px-4 py-2 num-cell">
                    {/* Round 8 · F2 — show the authoritative
                        stats.materiality_used only. Fallback to
                        s.parameters.materiality used to display
                        stale sample-input values (owner saw 5,000
                        while DB stored 0). "—" means the engine
                        didn't record a materiality at draw time. */}
                    {Number(s.stats?.materiality_used || 0) > 0
                      ? fmtNumber(Number(s.stats.materiality_used))
                      : "—"}
                    {s.stats?.materiality_source === "engagement_performance_materiality" && (
                      <div className="text-[9px] text-emerald-700">{t("sampling.source105a")}</div>
                    )}
                  </td>
                  <td className="px-4 py-2 num-cell">{s.population_size}</td>
                  <td className="px-4 py-2 num-cell">{s.sample_size}</td>
                  <td className="px-4 py-2 font-mono text-[10px] text-emerald-700">
                    {(s.stats && s.stats.uid_range) || "—"}
                  </td>
                  <td className="px-4 py-2 text-xs text-muted-foreground" data-testid={`sample-stats-${s.id}`}>
                    {s.stats?.coverage_label && <div className="font-medium text-emerald-700" data-testid={`sample-coverage-${s.id}`}>{s.stats.coverage_label}</div>}
                    {s.stats?.sampling_interval ? `${t("sampling.intervalPrefix")} ${fmtNumber(s.stats.sampling_interval)}` :
                     s.stats?.confidence_level ? `${t("sampling.confPrefix")} ${confidenceLabel(s.stats.confidence_level)}` : (s.stats?.n_basis || "—")}
                    {s.stats?.sampling_interval && s.stats?.confidence_level ? <div data-testid={`sample-conf-${s.id}`}>{t("sampling.confPrefix")} {confidenceLabel(s.stats.confidence_level)}</div> : null}   {/* Batch 20 · F-153 (B5): the level only */}
                    {s.stats?.n_computed != null && <div>n {fmtCount(s.stats.n_computed)} · {t("sampling.keyItems")} {s.stats.key_items} · {t("sampling.sampledItems")} {s.stats.sampled_items}</div>}
                    {s.stats?.n_used != null && s.stats?.n_computed == null && <div title={s.stats.n_basis}>n {s.stats.n_used}</div>}
                    {s.stats?.strata_boundaries && (
                      <div data-testid={`sample-strata-${s.id}`}>{s.stats.strata_boundaries.map((b) => `S${b.stratum} ${fmtNumber(b.from)}–${fmtNumber(b.to)} n ${b.n_h}/${b.N_h}`).join(" · ")} · Σ n {s.stats.strata_boundaries.reduce((a, b) => a + b.n_h, 0)} = {s.sample_size} · {t("sampling.keyItems")} {s.stats.key_items}{s.stats.allocation_note ? ` · ${s.stats.allocation_note}` : ""}</div>
                    )}
                    {s.stats?.method === "PPS" && <div>n {s.stats.sample_size} · {t("sampling.keyItems")} {s.stats.key_items} · {t("sampling.sampledItems")} {s.stats.sampled_items}</div>}
                    {s.stats?.reason_counts && (
                      <div data-testid={`sample-risk-${s.id}`}>{Object.entries(s.stats.reason_counts).map(([k, v]) => `${k} ${v}`).join(" · ")} · {t("sampling.keyItems")} {s.stats.key_items}</div>
                    )}
                    <C3cStatsLines s={s} t={t} />
                    <PopulationPeriodLine sample={s} t={t} lang={lang} />
                  </td>
                  <td className="px-4 py-2 text-end">
                    {s.stats?.population_dedupe?.removed > 0 ? (
                      <span title={`Population raw = ${fmtCount(s.stats.population_dedupe.raw_count)} rows. ${s.stats.population_dedupe.removed} main-GL row(s) superseded by head-wise siblings on composite key (voucher_no, account_code). Rule: ${s.stats.population_dedupe.rule}.`}
                            className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 font-mono text-[10px] font-semibold text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200"
                            data-testid={`sample-dedupe-${s.id}`}>
                        −{fmtCount(s.stats.population_dedupe.removed)}
                      </span>
                    ) : s.stats?.population_dedupe ? (
                      <span className="text-[10px] text-muted-foreground"
                            title={t("sampling.noHeadOverlapTitle")}
                            data-testid={`sample-dedupe-${s.id}`}>{t("sampling.noneTag")}</span>
                    ) : (
                      <span className="text-[10px] text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-end">
                    {s.needs_rerun && (
                      <Button size="sm" variant="outline" className="me-1 border-amber-400 bg-amber-50 text-amber-800 hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200"
                              onClick={async () => {
                                // Batch 22 · PR A follow-up: a MUS / Random / … sample flagged because its GL was deleted / replaced is
                                // re-run by re-drawing it from the current ledger (the Re-sample dialog, action "new") — the automatic
                                // /rerun route is for Continuous sheets only.
                                if (s.method !== "Continuous (100% Population)") { setResampleTarget(s); return; }
                                if (!await askConfirm(t("sampling.reRunConfirm").replace("{name}", s.name))) return;
                                try {
                                  const r = await api.post(`/samples/${s.id}/rerun`);
                                  const cnt = r.data?.sheets?.length || 0;
                                  toast.success(t("sampling.reRunCompleteToast").replace("{msg}", fmtPlural(cnt, lang, dict.plurals.freshSheets)));
                                  await loadSamples();
                                } catch (e) { toast.error(toErrMsg(e, t("sampling.reRunFailed"))); }
                              }}
                              data-testid={`samp-rerun-${s.id}`}>
                        <RefreshCw className="me-1 h-3 w-3" /> {t("sampling.reRunToast")}
                      </Button>
                    )}
                    {s.method !== "Continuous (100% Population)" && (
                      <Button size="sm" variant="outline" className="me-1"
                              onClick={() => setResampleTarget(s)}
                              disabled={!!current?.signed_off}
                              data-testid={`samp-resample-${s.id}`}>
                        <Layers className="me-1 h-3 w-3" /> {t("sampling.resampleBtn")}
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => exportSample(s.id)} data-testid={`samp-export-${s.id}`}>
                      <Download className="me-1 h-3 w-3" /> XLSX
                    </Button>
                    {s.stats?.headwise_coverage?.any && (
                      <>
                        <Button size="sm" variant="outline" className="ms-1"
                                onClick={() => exportHeadwise(s, "csv")}
                                title={t("sampling.downloadHeadCsvTitle")}
                                data-testid={`samp-hw-csv-${s.id}`}>
                          <Download className="me-1 h-3 w-3" /> {t("b3.hwCsv")}
                        </Button>
                        <Button size="sm" variant="outline" className="ms-1"
                                onClick={() => exportHeadwise(s, "xlsx")}
                                title={t("sampling.downloadHeadXlsxTitle")}
                                data-testid={`samp-hw-xlsx-${s.id}`}>
                          <Download className="me-1 h-3 w-3" /> {t("b3.hwXlsx")}
                        </Button>
                      </>
                    )}
                    {s.method !== "Continuous (100% Population)" && (
                      <Button size="sm" variant="outline" className="ms-1"
                              onClick={() => setEvalOpen(evalOpen === s.id ? "" : s.id)}
                              data-testid={`samp-evaluate-${s.id}`}>{t("s7a.panel.openBtn")}</Button>
                    )}
                    <Button size="sm" variant="outline" className="ms-1"
                            onClick={() => setSoOpen(soOpen === s.id ? "" : s.id)}
                            data-testid={`samp-signoffs-${s.id}`}>{t("itemSignoff.tabTitle")}</Button>
                    <Button size="sm" variant="outline" className="ms-1"
                            onClick={() => setAttachSample(s)}
                            data-testid={`samp-attach-${s.id}`}>
                      <Paperclip className="me-1 h-3 w-3" /> {t("sampling.attachBtn")}
                    </Button>
                    <Button size="sm" variant="ghost" className="ms-1 text-red-600"
                            onClick={async () => {
                              if (!await askConfirm(t("sampling.deleteSampleConfirm").replace("{name}", s.name))) return;
                              try {
                                await api.delete(`/samples/${s.id}`);
                                toast.success(t("sampling.sampleDeletedToast"));
                                await loadSamples();
                              } catch (e) { toast.error(toErrMsg(e, t("sampling.toasts.deleteFailed"))); }
                            }}
                            data-testid={`samp-delete-${s.id}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </td>
                </tr>
                {soOpen === s.id && (   /* Batch 11 · A2 — the sampling sheet's sign-off bar */
                  <tr data-testid={`sample-signoff-row-${s.id}`}><td colSpan={9} className="px-4 py-2">
                    <SignOffBar engId={current?.id} itemType="sampling" itemKey={s.id} label={t("itemSignoff.samplingSheet")} />
                  </td></tr>
                )}
                {evalOpen === s.id && (   /* Step 7a — the evaluation of the sample */
                  <tr data-testid={`sample-eval-row-${s.id}`}><td colSpan={9} className="px-4 py-2">
                    <EvaluationPanel sampleId={s.id} engId={current?.id} locked={!!current?.signed_off} />
                  </td></tr>
                )}
                </React.Fragment>
              ))}
              {samples.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-6 text-center text-muted-foreground">{t("sampling.noSamplesYet")}</td></tr>
              )}
            </tbody>
          </table>
          </div>
        </CardContent>
      </Card>

      <FullScreenDialog open={!!attachSample} onClose={() => setAttachSample(null)}
                        testid="sample-attach-dialog"
                        title={t("sampling.supportingDocsFor").replace("{name}", attachSample?.name || "")}>
          {attachSample && (
            <AttachmentPanel engagementId={current.id}
                              contextType="sample" contextId={attachSample.id}
                              locked={!!current.signed_off}
                              label={t("sampling.attachLabel")} />
          )}
      </FullScreenDialog>

      <HeadwiseGLDialog open={headwiseOpen}
                        onOpenChange={setHeadwiseOpen}
                        engagementId={current?.id}
                        onDone={async () => { await loadHeadUploads(); await loadSamples(); }} />

      <ResampleDialog open={!!resampleTarget}
                      onOpenChange={(v) => !v && setResampleTarget(null)}
                      sample={resampleTarget}
                      onOpenHeadwise={() => { setResampleTarget(null); setHeadwiseOpen(true); }}
                      onDone={async () => { setResampleTarget(null); await loadSamples(); }} />

      <HeadTimelineDialog open={!!timelineHead}
                          onOpenChange={(v) => !v && setTimelineHead(null)}
                          engagementId={current?.id}
                          fsCode={timelineHead}
                          onUndone={async () => { await loadHeadUploads(); await loadSamples(); }} />

      <SkippedRowsDialog open={!!skippedFor}
                          onOpenChange={(v) => !v && setSkippedFor(null)}
                          engagementId={current?.id}
                          uploadId={skippedFor} />
    </div>
  );
}
